import type { NomenclatureItem } from '@/features/common/types/NomenclatureItem';
import { domainLabel } from '@/features/common/useThematicDomains';
import type { AdvisorCoverage } from '@/features/publicsite/types/registry/AdvisorCoverage';
import type { DomainCoverageNote } from '@/features/publicsite/types/registry/DomainCoverageNote';
import type { DomainCoverageRow } from '@/features/publicsite/types/registry/DomainCoverageRow';

/** What a domain's row can say about its raions — see domainCoverageNote for why there are three. */
export const DOMAIN_COVERAGE_NOTE = {
  /** Nobody advises on this domain anywhere. Its raions are not the subject. */
  noAdvisors: 'NO_ADVISORS',
  /** Advisers, and raions that have advisers but none of them in this domain. */
  gaps: 'GAPS',
  /** Advisers, and no such raion. Says how many, and nothing about coverage. */
  advisorsOnly: 'ADVISORS_ONLY',
} as const;

/**
 * The coverage report per domain of competence: how many public advisers work in each, and which
 * raions have advisers but none of them in that domain.
 *
 * Driven by the nomenclature rather than by the response, because the two answer different
 * questions. `domains` counts the advisers there are, so a domain nobody advises on is simply
 * absent from it — and that is the single most useful line in a coverage report, the one saying an
 * entire subject area has no one at all. Listing every administered domain and joining the counts
 * onto it is what puts those back.
 *
 * The other way round, a domain id the nomenclature does not carry is dropped: it can only be an
 * item an administrator has retired, and a bare UUID on a public page tells a visitor strictly
 * less than leaving it out — the same call specializationNames makes for a provider's card.
 *
 * Ordered by adviser count, as the endpoint orders its own lists, with the name to break a tie so
 * that two domains on the same count cannot swap places between reads — the response's order
 * cannot be borrowed here, since the domains missing from it have no place in it.
 *
 * No domains at all — an empty nomenclature, or a backend older than T240 that does not send them
 * — gives no rows, and the caller draws nothing. It cannot give rows reading "0 advisers" instead:
 * that is a statement about the registry, and this would not be in a position to make it.
 */
export function domainCoverageRows(
  coverage: Pick<AdvisorCoverage, 'domains' | 'gapZonesByDomain'>,
  items: NomenclatureItem[],
  language: string,
): DomainCoverageRow[] {
  if (!coverage.domains) return [];
  const counts = new Map(coverage.domains.map((d) => [d.domainId, d.advisors]));
  const gaps = new Map<string, { territorialUnitId: string; name: string }[]>();
  (coverage.gapZonesByDomain ?? []).forEach((gap) => {
    const zones = gaps.get(gap.domainId) ?? [];
    zones.push({ territorialUnitId: gap.territorialUnitId, name: gap.name });
    gaps.set(gap.domainId, zones);
  });

  return items
    .map((item) => ({
      domainId: item.id,
      label: domainLabel(item, language),
      advisors: counts.get(item.id) ?? 0,
      gapZones: gaps.get(item.id) ?? [],
    }))
    .sort((a, b) => b.advisors - a.advisors || a.label.localeCompare(b.label, language));
}

/**
 * Which of three things a domain's row is in a position to say.
 *
 * An empty `gapZones` was being read as "no gaps", and printed as every raion covered. It does not
 * mean that. A gap is a pair of a raion and a domain, and CoverageService only makes one for a
 * raion that has advisers — so a domain nobody advises on has no pairs at all, and its empty list
 * means the opposite of coverage. That reading put "toate raioanele acoperite" on all seven rows of
 * a registry where no adviser had yet chosen a domain.
 *
 * The count decides first, for that reason: with no advisers there is nothing to say about raions,
 * whatever the list holds. With advisers and no pairs, the row says how many and stops — "every
 * raion covered" would still overstate it, since the gaps are only ever counted among the raions
 * that have advisers, and the ones with none are the other block's subject entirely.
 */
export function domainCoverageNote(
  row: Pick<DomainCoverageRow, 'advisors' | 'gapZones'>,
): DomainCoverageNote {
  if (row.advisors === 0) return DOMAIN_COVERAGE_NOTE.noAdvisors;
  return row.gapZones.length > 0 ? DOMAIN_COVERAGE_NOTE.gaps : DOMAIN_COVERAGE_NOTE.advisorsOnly;
}
